/**
 * PlotexSectionTool — section / cutaway clipping plane for arch-viz.
 *
 * Drag a world-aligned clipping plane through the scene to reveal interior
 * detail. Standard feature across CAD, Revit, SketchUp, Rhino — the viewer
 * is expected to offer it so clients can inspect structural cross-sections
 * without asking the authoring team to produce pre-cut variants.
 *
 * Implementation notes — three.js clipping semantics are subtle and worth
 * documenting because the sign convention has bitten more than one reader:
 *
 *   - `renderer.localClippingEnabled = true` is a HARD prerequisite. Setting
 *     clippingPlanes without flipping this flag silently no-ops.
 *   - A three.js `Plane(normal, constant)` is the plane
 *         { x | dot(normal, x) + constant = 0 }
 *     meaning the signed distance from the origin to the plane along the
 *     normal direction is `-constant`. A point `x` is CLIPPED (not rendered)
 *     when `dot(normal, x) + constant < 0` — i.e. when it sits on the side
 *     OPPOSITE the normal. Flipping which half is hidden is therefore as
 *     simple as negating the normal (and adjusting the constant accordingly
 *     so the plane stays at the same world position).
 *   - For a world-axis plane through x = offset with +X hidden, we use
 *     normal = (-1, 0, 0) and constant = offset. Points with x > offset then
 *     satisfy dot(normal, x) + constant = -x + offset < 0 → clipped.
 *   - To toggle which side is visible we flip normal -> -normal and
 *     constant -> -constant. The plane's world position is unchanged; only
 *     the "which half lives, which half dies" assignment swaps.
 *
 * Why we use `renderer.clippingPlanes` (global) instead of per-material
 * `material.clippingPlanes`: progressive meshes, InstancedMesh groups,
 * impostor quads, and freshly-fetched LOD swaps all get clipped uniformly
 * with zero per-mesh setup. Per-material clipping would require walking the
 * scene graph on every LOD swap — error-prone and wasteful.
 *
 * PlaneHelper is a standard three.js helper (tiny — a PlaneGeometry + a
 * LineSegments outline). We tag it with `userData.__plotexIgnoreRaycast`
 * so the measure tool and any other picker can filter it out when
 * raycasting — no need to register targets by name.
 */

import {
  Box3,
  Object3D,
  PerspectiveCamera,
  Plane,
  PlaneHelper,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three';

export type PlotexSectionAxis = 'x' | 'y' | 'z';

export interface PlotexSectionToolOptions {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  /**
   * Scene root to compute bounds against. Passing the loaded glTF root
   * (instead of the full three scene) keeps helper lights, skyboxes, and
   * the PlaneHelper itself out of the bbox — which would otherwise skew
   * the slider's min/max range.
   */
  target?: Object3D;
  /**
   * Button stack offset (px from the bottom of the viewport). Defaults to
   * 208 px so the section button lands above the walk/measure/quality
   * stack. Callers can override if the toolbar stack changes.
   */
  buttonBottom?: number;
}

// Accent color also used by the line / helper grid so active state reads
// consistently across the UI. Matches the measure-tool's accent blue.
const ACCENT_COLOR = 0x4a9eff;
// Slight padding on the computed slider range so the user can push the
// plane a tad past the geometry and still see the scene unclipped.
const RANGE_PADDING = 0.05;

export class PlotexSectionTool {
  private scene: Scene;
  private camera: PerspectiveCamera;
  private renderer: WebGLRenderer;
  private target: Object3D;
  private buttonBottom: number;

  private active = false;
  private axis: PlotexSectionAxis = 'x';
  // `flip` toggles which side of the plane is clipped. When false we hide
  // the +axis half (normal points in -axis direction); when true we hide
  // the -axis half. Toggled via the Flip button in the panel.
  private flip = false;
  // Plane offset along the chosen axis in world units. Kept as a scalar
  // so we can re-derive the Plane's normal+constant on axis change without
  // losing the user's chosen position.
  private offset = 0;
  // Cached scene bounding box. Recomputed on activate() so late-loading
  // LODs that extend the bounds stay within the slider range even if the
  // user toggled the tool on after an initial paint.
  private boundsMin = new Vector3();
  private boundsMax = new Vector3();

  // Three.js objects we own and must dispose.
  private plane: Plane;
  private planeHelper: PlaneHelper;
  // PlaneHelper's visualized extent — sized to ~1.2x the scene diameter so
  // the helper always visibly crosses the whole scene regardless of the
  // axis chosen.
  private helperSize = 1;

  // UI elements we own.
  private button: HTMLButtonElement;
  private panel: HTMLDivElement;
  private axisPills: Record<PlotexSectionAxis, HTMLButtonElement>;
  private slider: HTMLInputElement;
  private readout: HTMLSpanElement;
  private flipButton: HTMLButtonElement;

  constructor(opts: PlotexSectionToolOptions) {
    this.scene = opts.scene;
    this.camera = opts.camera;
    this.renderer = opts.renderer;
    this.target = opts.target ?? opts.scene;
    // Default button offset stacks ABOVE the VR button (which occupies
    // bottom: 208px when cfg.xr.enabled). Each button is 40 px tall with
    // 24 px of breathing room above: Quality 16, Measure 80, Walk 144,
    // VR 208, Section 272 — consistent 64 px vertical rhythm.
    this.buttonBottom = opts.buttonBottom ?? 272;

    // localClippingEnabled is a one-time renderer switch. Flipping it here
    // (in the constructor) rather than on activate() keeps the toggle cost
    // to zero on activate/deactivate and avoids a per-frame check inside
    // WebGLRenderer for a feature that's cheap to leave on standby. When
    // our plane isn't in `renderer.clippingPlanes`, no clipping happens.
    this.renderer.localClippingEnabled = true;

    // Default plane: along world X, offset 0, hiding the +X half. The
    // offset + axis will be re-synced to the scene bbox midpoint inside
    // activate() so the user sees a meaningful cut on first toggle.
    this.plane = new Plane(new Vector3(-1, 0, 0), 0);

    // Helper visualizes the plane's extent as a translucent quad + outline.
    // Size is finalized when we compute the bbox in activate(); the initial
    // `1` here is just a placeholder so the helper exists in the graph.
    this.planeHelper = new PlaneHelper(this.plane, this.helperSize, ACCENT_COLOR);
    // Mark the helper so the measure tool (and any future picker) can
    // filter it out of raycasts. We also give it a recognizable name for
    // debugging via `scene.children` in devtools.
    this.planeHelper.name = 'plotex-section-plane-helper';
    this.planeHelper.userData.__plotexIgnoreRaycast = true;
    // The PlaneHelper itself is a group that contains a Mesh (the
    // translucent quad) and a LineSegments (the outline). Propagate the
    // ignore flag to children so name-based AND flag-based raycast filters
    // both end up skipping the helper's sub-objects.
    this.planeHelper.traverse((child: Object3D) => {
      child.userData.__plotexIgnoreRaycast = true;
    });
    this.planeHelper.visible = false;
    this.scene.add(this.planeHelper);

    // Build the DOM. Panel starts hidden — only visible while active.
    this.button = this._buildToolbarButton();
    document.body.appendChild(this.button);

    const panelBuilt = this._buildPanel();
    this.panel = panelBuilt.root;
    this.axisPills = panelBuilt.axisPills;
    this.slider = panelBuilt.slider;
    this.readout = panelBuilt.readout;
    this.flipButton = panelBuilt.flipButton;
    document.body.appendChild(this.panel);
  }

  // -- Public API -----------------------------------------------------------

  activate(): void {
    if (this.active) return;
    this.active = true;

    // Recompute bounds + slider range each time the user activates. LODs
    // that streamed in AFTER initial paint may have expanded the scene,
    // so refreshing here avoids a slider capped to the first-frame bbox.
    this._recomputeBounds();
    // Seed offset to the midpoint of the active axis so the first cut
    // actually reveals something rather than landing on empty space at the
    // edge of the bbox.
    const mid = this._axisMid(this.axis);
    this.offset = mid;
    this._syncPlaneFromState();
    this._syncSliderFromState();
    this._syncAxisPills();
    this._syncFlipButton();
    this._syncReadout();

    // Push our plane onto the renderer's global clippingPlanes. We use
    // push (not assignment) so other subsystems that want to add their own
    // clipping planes in the future can coexist; dispose()/deactivate()
    // removes only our entry.
    const idx = this.renderer.clippingPlanes.indexOf(this.plane);
    if (idx === -1) this.renderer.clippingPlanes.push(this.plane);

    this.planeHelper.visible = true;
    this.panel.style.display = 'flex';
    this._setButtonActive(true);
  }

  deactivate(): void {
    if (!this.active) return;
    this.active = false;

    // Remove our plane from the global clipping list. We intentionally
    // leave renderer.localClippingEnabled=true — it's harmless when the
    // plane array is empty and keeps activate() a cheap no-alloc toggle.
    const idx = this.renderer.clippingPlanes.indexOf(this.plane);
    if (idx !== -1) this.renderer.clippingPlanes.splice(idx, 1);

    this.planeHelper.visible = false;
    this.panel.style.display = 'none';
    this._setButtonActive(false);
  }

  setAxis(axis: PlotexSectionAxis): void {
    if (axis === this.axis) return;
    this.axis = axis;
    // When switching axes we rescale the slider and snap offset to the new
    // axis midpoint. Keeping the old offset would likely land the plane
    // outside the new axis's extent and confuse the user.
    this.offset = this._axisMid(axis);
    this._syncPlaneFromState();
    this._syncSliderFromState();
    this._syncAxisPills();
    this._syncReadout();
  }

  setOffset(offset: number): void {
    this.offset = offset;
    this._syncPlaneFromState();
    // Update the slider only if the programmatic offset doesn't match
    // what the slider already shows — prevents feedback loops when the
    // slider input event calls us.
    if (parseFloat(this.slider.value) !== offset) {
      this.slider.value = String(offset);
    }
    this._syncReadout();
  }

  setFlip(flip: boolean): void {
    if (flip === this.flip) return;
    this.flip = flip;
    this._syncPlaneFromState();
    this._syncFlipButton();
  }

  getOffset(): number {
    return this.offset;
  }

  getAxis(): PlotexSectionAxis {
    return this.axis;
  }

  isActive(): boolean {
    return this.active;
  }

  dispose(): void {
    this.deactivate();
    // Tear down the helper. PlaneHelper's traverse walks its own children,
    // which include a PlaneGeometry Mesh and a LineSegments outline — both
    // carry geometry + material references that need explicit dispose()
    // calls to release the VBOs + shader programs.
    this.scene.remove(this.planeHelper);
    this.planeHelper.traverse((child: any) => {
      if (child.geometry?.dispose) child.geometry.dispose();
      if (child.material?.dispose) child.material.dispose();
    });
    this.panel.remove();
    this.button.remove();
  }

  // -- State sync helpers ---------------------------------------------------

  /**
   * Recompute the scene bounding box into boundsMin / boundsMax and
   * derive the PlaneHelper size. Called on activate() so late-streaming
   * meshes are picked up naturally.
   */
  private _recomputeBounds(): void {
    const box = new Box3().setFromObject(this.target);
    if (box.isEmpty()) {
      // Defensive fallback: the user activated the tool against an empty
      // scene. Pretend we have a 10-unit-wide box around the origin so the
      // slider still functions and the helper is visible.
      this.boundsMin.set(-5, -5, -5);
      this.boundsMax.set(5, 5, 5);
    } else {
      this.boundsMin.copy(box.min);
      this.boundsMax.copy(box.max);
    }
    const size = new Vector3().subVectors(this.boundsMax, this.boundsMin);
    // PlaneHelper's `size` argument is the half-length of each edge of the
    // visualized quad. We size it at 1.2× the diagonal so the quad visibly
    // overflows the scene on any axis — the user's cue is "look, the plane
    // really does extend forever". Clamped to 1 to avoid a degenerate helper
    // on tiny scenes (single props).
    this.helperSize = Math.max(1, size.length() * 1.2);
    // PlaneHelper exposes `size` as a public field; updating it takes effect
    // on the next frame (three.js rebuilds the helper's line positions).
    (this.planeHelper as any).size = this.helperSize;
  }

  /**
   * Rewrite this.plane.normal and this.plane.constant from the current
   * axis + offset + flip state. See the top-of-file rubber-duck block for
   * the sign convention.
   */
  private _syncPlaneFromState(): void {
    // Choose the world-axis normal. The "default" (flip=false) hides the
    // +axis half, so the normal points in the -axis direction.
    let nx = 0;
    let ny = 0;
    let nz = 0;
    if (this.axis === 'x') nx = this.flip ? 1 : -1;
    else if (this.axis === 'y') ny = this.flip ? 1 : -1;
    else nz = this.flip ? 1 : -1;

    // With normal pointing in -axis (flip=false), a point with axis > offset
    // has dot(normal, x) + constant = -axis + offset. For that to be < 0
    // (clipped) we need axis > offset, which matches the "hide +axis half"
    // contract when constant = offset. When flipped, normal is +axis and
    // constant must be -offset to keep the plane at the same world position.
    const constant = this.flip ? -this.offset : this.offset;

    this.plane.normal.set(nx, ny, nz);
    this.plane.constant = constant;
  }

  private _syncSliderFromState(): void {
    const [min, max] = this._axisRange(this.axis);
    this.slider.min = String(min);
    this.slider.max = String(max);
    // Step size is 0.1% of the range — gives ~1000 distinct positions, which
    // feels smooth on any reasonable scene while keeping the numeric readout
    // from jittering in the third decimal.
    this.slider.step = String(Math.max(0.001, (max - min) * 0.001));
    this.slider.value = String(this.offset);
  }

  private _syncAxisPills(): void {
    for (const a of ['x', 'y', 'z'] as PlotexSectionAxis[]) {
      this._stylePill(this.axisPills[a], a === this.axis);
    }
  }

  private _syncFlipButton(): void {
    this.flipButton.setAttribute('aria-pressed', this.flip ? 'true' : 'false');
    if (this.flip) {
      this.flipButton.style.background = '#3b82f6';
      this.flipButton.style.color = '#fff';
    } else {
      this.flipButton.style.background = 'rgba(0, 0, 0, 0.55)';
      this.flipButton.style.color = '#ccc';
    }
  }

  private _syncReadout(): void {
    this.readout.textContent = `${this.offset.toFixed(2)} m`;
  }

  private _axisRange(axis: PlotexSectionAxis): [number, number] {
    // Pull axis extents with a small padding so the user can push the plane
    // just past the geometry if they want a totally unclipped view.
    const min = axis === 'x' ? this.boundsMin.x : axis === 'y' ? this.boundsMin.y : this.boundsMin.z;
    const max = axis === 'x' ? this.boundsMax.x : axis === 'y' ? this.boundsMax.y : this.boundsMax.z;
    const span = max - min || 1;
    const pad = span * RANGE_PADDING;
    return [min - pad, max + pad];
  }

  private _axisMid(axis: PlotexSectionAxis): number {
    const [min, max] = this._axisRange(axis);
    return (min + max) * 0.5;
  }

  // -- DOM builders ---------------------------------------------------------

  private _buildToolbarButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = 'Section';
    btn.setAttribute('aria-pressed', 'false');
    btn.title = 'Section plane (toggle a draggable clipping plane)';
    Object.assign(btn.style, {
      position: 'fixed',
      bottom: `${this.buttonBottom}px`,
      right: '16px',
      zIndex: '9999',
      width: '40px',
      height: '40px',
      padding: '0',
      borderRadius: '8px',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      background: 'rgba(0, 0, 0, 0.55)',
      color: '#ccc',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '10px',
      fontWeight: '500',
      letterSpacing: '0.2px',
      transition: 'background 0.15s ease, color 0.15s ease',
      userSelect: 'none',
    });
    btn.addEventListener('click', () => {
      if (this.active) this.deactivate();
      else this.activate();
    });
    btn.addEventListener('mouseenter', () => {
      if (!this.active) btn.style.background = 'rgba(255, 255, 255, 0.15)';
    });
    btn.addEventListener('mouseleave', () => {
      if (!this.active) btn.style.background = 'rgba(0, 0, 0, 0.55)';
    });
    return btn;
  }

  private _setButtonActive(active: boolean): void {
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (active) {
      this.button.style.background = '#3b82f6';
      this.button.style.color = '#fff';
    } else {
      this.button.style.background = 'rgba(0, 0, 0, 0.55)';
      this.button.style.color = '#ccc';
    }
  }

  private _buildPanel(): {
    root: HTMLDivElement;
    axisPills: Record<PlotexSectionAxis, HTMLButtonElement>;
    slider: HTMLInputElement;
    readout: HTMLSpanElement;
    flipButton: HTMLButtonElement;
  } {
    const root = document.createElement('div');
    Object.assign(root.style, {
      position: 'fixed',
      // Sits to the LEFT of the toolbar buttons. 72 px = 40 (button width)
      // + 16 (toolbar right margin) + 16 (gap) so the panel never overlaps
      // the stacked buttons.
      right: '72px',
      bottom: '16px',
      width: '240px',
      padding: '12px',
      background: 'rgba(15, 18, 24, 0.9)',
      color: '#e6e9ef',
      borderRadius: '8px',
      border: '1px solid rgba(74, 158, 255, 0.25)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '12px',
      zIndex: '9999',
      display: 'none',
      flexDirection: 'column',
      gap: '10px',
      userSelect: 'none',
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.45)',
    });

    // Axis pill row. Three 1-letter buttons for X / Y / Z.
    const axisRow = document.createElement('div');
    Object.assign(axisRow.style, {
      display: 'flex',
      gap: '6px',
      justifyContent: 'space-between',
    });
    const axisPills = {} as Record<PlotexSectionAxis, HTMLButtonElement>;
    for (const a of ['x', 'y', 'z'] as PlotexSectionAxis[]) {
      const pill = document.createElement('button');
      pill.textContent = a.toUpperCase();
      pill.setAttribute('aria-label', `Cut along ${a.toUpperCase()} axis`);
      Object.assign(pill.style, {
        flex: '1',
        height: '28px',
        padding: '0',
        borderRadius: '6px',
        border: '1px solid rgba(74, 158, 255, 0.25)',
        cursor: 'pointer',
        background: 'rgba(0, 0, 0, 0.55)',
        color: '#ccc',
        fontFamily: 'inherit',
        fontSize: '12px',
        fontWeight: '600',
        letterSpacing: '0.3px',
        transition: 'background 0.15s ease, color 0.15s ease',
      });
      pill.addEventListener('click', () => this.setAxis(a));
      axisRow.appendChild(pill);
      axisPills[a] = pill;
    }
    root.appendChild(axisRow);

    // Offset slider + numeric readout. We wrap them in a column so the
    // readout sits below the slider with a tight gap.
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '1';
    slider.step = '0.01';
    slider.value = '0.5';
    Object.assign(slider.style, {
      width: '100%',
      // touch-action ensures mobile users get a pure slider drag without
      // the browser fighting for scroll/pan. Critical on touch devices.
      touchAction: 'manipulation',
      accentColor: '#4a9eff',
    });
    slider.addEventListener('input', () => {
      const v = parseFloat(slider.value);
      if (Number.isFinite(v)) this.setOffset(v);
    });
    root.appendChild(slider);

    const readoutRow = document.createElement('div');
    Object.assign(readoutRow.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '11px',
      color: '#9aa4b2',
      letterSpacing: '0.2px',
    });
    const readoutLabel = document.createElement('span');
    readoutLabel.textContent = 'Offset';
    const readout = document.createElement('span');
    readout.textContent = '0.00 m';
    readout.style.color = '#e6e9ef';
    readout.style.fontVariantNumeric = 'tabular-nums';
    readoutRow.appendChild(readoutLabel);
    readoutRow.appendChild(readout);
    root.appendChild(readoutRow);

    // Flip toggle. Spans full width; styling mirrors the axis pills so the
    // whole panel reads as one control surface rather than a grab-bag.
    const flipButton = document.createElement('button');
    flipButton.textContent = 'Flip side';
    flipButton.setAttribute('aria-pressed', 'false');
    flipButton.title = 'Swap which side of the plane is clipped';
    Object.assign(flipButton.style, {
      width: '100%',
      height: '28px',
      padding: '0',
      borderRadius: '6px',
      border: '1px solid rgba(74, 158, 255, 0.25)',
      cursor: 'pointer',
      background: 'rgba(0, 0, 0, 0.55)',
      color: '#ccc',
      fontFamily: 'inherit',
      fontSize: '11px',
      fontWeight: '600',
      letterSpacing: '0.3px',
      transition: 'background 0.15s ease, color 0.15s ease',
    });
    flipButton.addEventListener('click', () => this.setFlip(!this.flip));
    root.appendChild(flipButton);

    return { root, axisPills, slider, readout, flipButton };
  }

  private _stylePill(pill: HTMLButtonElement, active: boolean): void {
    if (active) {
      pill.style.background = '#3b82f6';
      pill.style.color = '#fff';
      pill.style.borderColor = '#3b82f6';
    } else {
      pill.style.background = 'rgba(0, 0, 0, 0.55)';
      pill.style.color = '#ccc';
      pill.style.borderColor = 'rgba(74, 158, 255, 0.25)';
    }
  }
}
